import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Calendar, Users, Save, X, Edit2, Check, Download, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../networking/axiosconfig';
import Navbar from './Navbar';

// Relative path — axiosInstance supplies the origin and the auth token.
const LOCAL_API_BASE_URL = '/api';

const extractMonthlySalary = (response) => {
  const payload = response?.data;
  if (!payload) return null;

  const coerce = (value) => {
    if (value === undefined || value === null) return null;
    const num = parseFloat(value);
    return Number.isNaN(num) ? null : num;
  };

  if (typeof payload.monthlySalary === 'number' || typeof payload.monthlySalary === 'string') {
    return coerce(payload.monthlySalary);
  }

  if (typeof payload.monthly_salary === 'number' || typeof payload.monthly_salary === 'string') {
    return coerce(payload.monthly_salary);
  }

  if (payload.salary !== undefined) {
    if (typeof payload.salary === 'number' || typeof payload.salary === 'string') {
      return coerce(payload.salary);
    }
    const salaryObj = payload.salary || {};
    if (salaryObj.monthly_salary !== undefined) return coerce(salaryObj.monthly_salary);
    if (salaryObj.monthlySalary !== undefined) return coerce(salaryObj.monthlySalary);
  }

  if (Array.isArray(payload.data) && payload.data.length > 0) {
    const latestRecord = payload.data[0];
    if (latestRecord.monthly_salary !== undefined) return coerce(latestRecord.monthly_salary);
    if (latestRecord.monthlySalary !== undefined) return coerce(latestRecord.monthlySalary);
  }

  return null;
};

const buildDefaultAttendance = (monthName, year, helpers, isCurrentMonth = false) => {
  const { getDaysInMonth, isSunday, getHolidayInfo, getDayName } = helpers;
  const daysInMonth = getDaysInMonth(monthName, year);
  return Array.from({ length: daysInMonth }, (_, index) => {
    const date = index + 1;
    if (isSunday(date, monthName, year)) {
      return { date, status: 'sunday', dayName: 'Sun', isHoliday: false };
    }
    const holidayInfo = getHolidayInfo(date, monthName, year);
    if (holidayInfo) {
      return {
        date,
        status: 'holiday',
        dayName: getDayName(date, monthName, year),
        isHoliday: true,
        holidayName: holidayInfo.name
      };
    }

    if (isCurrentMonth) {
      return {
        date,
        status: '',
        dayName: getDayName(date, monthName, year),
        isHoliday: false
      };
    }
    // For past months, default to present
    return {
      date,
      status: 'present',
      dayName: getDayName(date, monthName, year),
      isHoliday: false
    };
  });
};

const transformApiAttendance = (records, monthName, year, helpers) => {
  const { getDayName, getHolidayInfo } = helpers;
  return records
    .map((record) => {
      const rawDate = record.date || record.attendance_date;
      if (!rawDate) return null;
      const parsedDate = new Date(rawDate);
      if (Number.isNaN(parsedDate.getTime())) return null;
      const day = parsedDate.getDate();
      const status = (record.status || '').toLowerCase();
      const holidayInfo = getHolidayInfo(day, monthName, year);
      const recordHolidayName = record.holiday_name || record.holidayName || record.name;
      const finalHolidayName = recordHolidayName || holidayInfo?.name;
      const isHolidayDay = status === 'holiday' || Boolean(holidayInfo);
      return {
        date: day,
        status,
        dayName: getDayName(day, monthName, year),
        isHoliday: isHolidayDay,
        holidayName: isHolidayDay ? (finalHolidayName || 'Holiday') : undefined
      };
    })
    .filter(Boolean);
};

const fetchAttendanceFromApi = async (userId, month, year) => {
  try {
    const response = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/attendance`, {
      params: { userId, month, year }
    });
    const data = response?.data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.attendance)) return data.attendance;
    return [];
  } catch (error) {
    if (error?.response?.status !== 404) {
      console.error(`Failed to fetch attendance for user ${userId}:`, error.message || error);
    }
    return [];
  }
};

const fetchSalarySummaryFromApi = async (userId, month, year) => {
  try {
    const response = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/netsalary`, {
      params: { userId, month, year }
    });
    const rows = response?.data?.data;
    if (Array.isArray(rows) && rows.length > 0) {
      const latest = rows[0];
      const parseNumber = (value) => {
        if (value === undefined || value === null) return null;
        const num = parseFloat(value);
        return Number.isNaN(num) ? null : num;
      };
      return {
        netSalary: parseNumber(latest.net_salary ?? latest.netSalary),
        deduction: parseNumber(latest.salary_cut ?? latest.salaryCut),
        extraPay: parseNumber(latest.extra_pay ?? latest.extraPay),
        bonus: parseNumber(latest.bonus ?? latest.bonus),
        bonusReason: latest.bonus_reason ?? latest.bonusReason ?? null,
        perDaySalary: parseNumber(latest.per_day_salary ?? latest.perDaySalary),
        monthlySalary: parseNumber(latest.monthly_salary ?? latest.monthlySalary),
        raw: latest
      };
    }
    return null;
  } catch (error) {
    if (error?.response?.status !== 404) {
      console.error(`Failed to fetch salary summary for user ${userId}:`, error.message || error);
    }
    return null;
  }
};

const buildHolidayMap = (records = [], monthNames) => {
  const map = {};
  records.forEach((holiday) => {
    const dateObj = new Date(holiday.date);
    if (Number.isNaN(dateObj.getTime())) return;
    const year = dateObj.getFullYear();
    const monthName = monthNames[dateObj.getMonth()];
    const day = dateObj.getDate();

    if (!map[year]) map[year] = {};
    if (!map[year][monthName]) map[year][monthName] = {};
    map[year][monthName][day] = {
      day,
      name: holiday.holiday_name || holiday.holidayName || holiday.name || 'Holiday',
      raw: holiday
    };
  });
  return map;
};


const SalaryCalculationSystem = () => {
  const navigate = useNavigate();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const currentDate = new Date();
  const currentCalendarYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();
  const previousMonthIndex = currentMonthIndex === 0 ? 11 : currentMonthIndex - 1;
  const previousYear = currentMonthIndex === 0 ? currentCalendarYear - 1 : currentCalendarYear;
  const defaultMonthName = monthNames[previousMonthIndex];

  const [selectedMonth, setSelectedMonth] = useState(defaultMonthName);
  const [selectedYear, setSelectedYear] = useState(previousYear);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [attendanceSaving, setAttendanceSaving] = useState(false);
  const [attendanceError, setAttendanceError] = useState(null);
  const [quarterLeaveData, setQuarterLeaveData] = useState(null);
  const [quarterLeaveLoading, setQuarterLeaveLoading] = useState(false);
  const [salaryUpdateState, setSalaryUpdateState] = useState({});
  const [employees, setEmployees] = useState([]);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [bonus, setBonus] = useState("");
  const [bonusReason, setBonusReason] = useState("");
  const [bonusSubmitting, setBonusSubmitting] = useState(false);
  const [hasExistingBonus, setHasExistingBonus] = useState(false);
  const bonusInputRef = useRef(null);
  const [salarySlipGenerating, setSalarySlipGenerating] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);

  const financialYearMonths = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March'
  ];

  const buildYearOptions = () => {
    const startYear = currentCalendarYear - 10;
    const endYear = currentCalendarYear + 50;
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  const availableYears = buildYearOptions();

  const [publicHolidays, setPublicHolidays] = useState({});
  const employeeCardRefs = useRef({});
  const modalScrollRef = useRef(null);
  const lastWorkedEmployeeIdRef = useRef(null);
  const scrollPositionBeforeModalRef = useRef(0);

  const getHolidayInfo = (date, month, year) =>
    publicHolidays[year]?.[month]?.[date] || null;

  // Fetch available months from salary records
  const fetchAvailableMonths = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      // Fetch all employees first
      const employeesResponse = await axiosInstance.get('/api/users', {
        headers: {
          Authorization: `Bearer ${token.trim()}`
        }
      });

      const employeesData = employeesResponse.data?.data || [];
      const activeEmployees = employeesData.filter(emp => emp.is_active !== 1);

      // Fetch salary records for all employees to find available months
      const monthYearSet = new Set();

      await Promise.all(
        activeEmployees.map(async (emp) => {
          try {
            // Check last 12 months for salary data
            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth() + 1;

            for (let i = 0; i < 12; i++) {
              const checkDate = new Date(currentYear, currentMonth - 1 - i, 1);
              const checkMonth = checkDate.getMonth() + 1;
              const checkYear = checkDate.getFullYear();

              try {
                const salaryResponse = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/netsalary`, {
                  params: { userId: emp.user_id_PK, month: checkMonth, year: checkYear }
                });

                if (salaryResponse?.data?.data && Array.isArray(salaryResponse.data.data) && salaryResponse.data.data.length > 0) {
                  const monthName = monthNames[checkMonth - 1];
                  monthYearSet.add(`${monthName}_${checkYear}`);
                }
              } catch (err) {

              }
            }
          } catch (err) {

          }
        })
      );

      // Convert Set to array and sort
      const availableMonthsList = Array.from(monthYearSet).map(item => {
        const [monthName, year] = item.split('_');
        return { monthName, year: parseInt(year) };
      }).sort((a, b) => {
        // Sort by year first, then by month
        if (a.year !== b.year) return b.year - a.year; // Descending year
        const monthA = monthNames.indexOf(a.monthName);
        const monthB = monthNames.indexOf(b.monthName);
        return monthB - monthA; // Descending month
      });

      setAvailableMonths(availableMonthsList);

      // Only change month if the currently selected month is not available
      // Don't change if it's the default month (previous month) - keep it as default
      if (availableMonthsList.length > 0) {
        const currentSelected = `${selectedMonth}_${selectedYear}`;
        const isAvailable = availableMonthsList.some(m => `${m.monthName}_${m.year}` === currentSelected);
        const isDefaultMonth = selectedMonth === defaultMonthName && selectedYear === previousYear;

        // Only change if current month is not available AND it's not the default month
        // This prevents changing from default month (November) to first available (December)
        if (!isAvailable && !isDefaultMonth) {
          const firstAvailable = availableMonthsList[0];
          setSelectedMonth(firstAvailable.monthName);
          setSelectedYear(firstAvailable.year);
        }
        // If default month is not available, still keep it (don't auto-change to December)
      }
    } catch (err) {
      console.error('Error fetching available months:', err);
      // On error, show all months as fallback
      setAvailableMonths([]);
    }
  };

  const attachAttendanceToEmployees = async (employeeList, monthName, year) => {
    const monthNumber = getMonthNumber(monthName);

    const helpers = {
      getDaysInMonth,
      isSunday,
      isPublicHoliday,
      getDayName,
      getHolidayInfo
    };

    // Check if this is the current month
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const isCurrentMonth = monthNumber === currentMonth && year === currentYear;

    const updatedEmployees = await Promise.all(
      employeeList.map(async (emp) => {
        const apiRecords = await fetchAttendanceFromApi(emp.id, monthNumber, year);
        const attendanceEntries = apiRecords.length
          ? transformApiAttendance(apiRecords, monthName, year, helpers)
          : buildDefaultAttendance(monthName, year, helpers, isCurrentMonth);

        const stats = calculateAttendanceStats(attendanceEntries);

        let salarySummary = null;
        let calculatedSalaryDetails = null;

        if (apiRecords.length > 0) {
          try {
            // Recalculate salary based on current attendance to ensure accuracy
            const salaryResponse = await axiosInstance.post(`${LOCAL_API_BASE_URL}/salary/calculate`, {
              userId: emp.id,
              month: monthNumber,
              year,
            });
            calculatedSalaryDetails = salaryResponse.data?.salaryDetails || {};
          } catch (calcError) {
            console.error(`Error recalculating salary for user ${emp.id}:`, calcError);
            // Fallback to fetching from API if calculation fails
            salarySummary = await fetchSalarySummaryFromApi(emp.id, monthNumber, year);
          }
        } else {
          // No attendance data, just fetch salary summary
          salarySummary = await fetchSalarySummaryFromApi(emp.id, monthNumber, year);
        }

        if (calculatedSalaryDetails) {
          salarySummary = calculatedSalaryDetails;
        }


        // Use calculated salary details if available, otherwise use fetched summary
        const parseSafe = (value, fallback = 0) => {
          if (value === null || value === undefined) return fallback;
          const parsed = parseFloat(value);
          return Number.isNaN(parsed) ? fallback : parsed;
        };

        const finalNetSalary = calculatedSalaryDetails
          ? parseSafe(calculatedSalaryDetails.netSalary, emp.baseSalary)
          : (salarySummary?.netSalary ?? emp.baseSalary);
        const finalDeduction = calculatedSalaryDetails
          ? parseSafe(calculatedSalaryDetails.salaryCut, 0)
          : (salarySummary?.deduction ?? 0);
        const finalExtraPay = calculatedSalaryDetails
          ? parseSafe(calculatedSalaryDetails.extraPay, 0)
          : (salarySummary?.extraPay ?? 0);
        // Always use bonus from salarySummary (fetched from DB) as it's the most reliable source
        const finalBonus = parseSafe(salarySummary?.bonus, 0);

        const previousMonthData = emp.monthlyData[monthName] || {};

        return {
          ...emp,
          monthlyData: {
            ...emp.monthlyData,
            [monthName]: {
              ...(emp.monthlyData[monthName] || {}),
              attendance: attendanceEntries,
              present: stats.present,
              cl: stats.cl,
              pl: stats.pl,
              absent: stats.absent,
              sunday: stats.sunday,
              holiday: stats.holiday,
              netSalary: finalNetSalary,
              deduction: finalDeduction,
              extraPay: finalExtraPay,
              bonus: finalBonus,
              serverSalary: salarySummary || {
                netSalary: finalNetSalary,
                deduction: finalDeduction,
                extraPay: finalExtraPay,
                bonus: finalBonus,
                perDaySalary: calculatedSalaryDetails?.perDaySalary || salarySummary?.perDaySalary,
                monthlySalary: calculatedSalaryDetails?.monthlySalary || salarySummary?.monthlySalary || emp.baseSalary
              }
            }
          }
        };
      })
    );

    return updatedEmployees;
  };

  // Fetch employees with their salaries from backend
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const response = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/holiday`);
        const records = response.data?.data || [];
        setPublicHolidays(buildHolidayMap(records, monthNames));
      } catch (err) {
        console.error('Failed to fetch holidays:', err);
      }
    };

    fetchHolidays();
    fetchAvailableMonths();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      const token = localStorage.getItem("token");

      if (!token || !token.trim()) {
        setError('Please login to access this page.');
        setLoading(false);
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 2000);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch employees
        const response = await axiosInstance.get('/api/users', {
          headers: {
            Authorization: `Bearer ${token.trim()}`
          }
        });

        const employeesData = response.data?.data || [];

        // Filter out inactive employees (is_active === 1)
        const activeEmployees = employeesData.filter(emp => emp.is_active !== 1);

        const transformedEmployees = await Promise.all(
          activeEmployees.map(async (emp) => {
            let avatar = '👤';
            if (emp.gender === 'Male') avatar = '👨';
            else if (emp.gender === 'Female') avatar = '👩';

            let baseSalary = emp.base_salary || 0;
            try {
              const salaryResponse = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/salary`, {
                params: { userId: emp.user_id_PK }
              });
              const savedSalary = extractMonthlySalary(salaryResponse);
              if (savedSalary) {
                baseSalary = parseFloat(savedSalary);
              }
            } catch (salaryErr) {
              if (salaryErr?.response?.status !== 404) {
                console.error(`Error fetching salary for user ${emp.user_id_PK}:`, salaryErr.message);
              }
            }

            return {
              id: emp.user_id_PK,
              name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Unknown',
              avatar: emp.user_profile_img || avatar,
              baseSalary,
              monthlyData: {},
              email: emp.email,
              designation: emp.designation_name,
              department: emp.department_name,
              isActive: emp.is_active === 0
            };
          })
        );

        const yearForSelectedMonth = getMonthYear(selectedMonth);
        const employeesWithAttendance = await attachAttendanceToEmployees(
          transformedEmployees,
          selectedMonth,
          yearForSelectedMonth
        );

        setEmployees(employeesWithAttendance);
      } catch (err) {
        console.error('Error fetching employees:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to load employees.';
        setError(errorMessage);

        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("token");
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 2000);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    if (!employees.length) return;
    const year = getMonthYear(selectedMonth);

    const refreshAttendance = async () => {
      const updated = await attachAttendanceToEmployees(employees, selectedMonth, year);
      setEmployees(updated);
    };

    refreshAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, publicHolidays]);

  const getMonthYear = (month) => {
    const monthIndex = financialYearMonths.indexOf(month);
    return monthIndex <= 8 ? selectedYear : selectedYear + 1;
  };

  const getMonthNumber = (monthName) => monthNames.indexOf(monthName) + 1;

  const formatDateForApi = (day, monthName, year) => {
    const monthNumber = getMonthNumber(monthName);
    if (monthNumber < 1) return '';
    const paddedMonth = String(monthNumber).padStart(2, '0');
    const paddedDay = String(day).padStart(2, '0');
    return `${year}-${paddedMonth}-${paddedDay}`;
  };

  const getDaysInMonth = (month, year) => {
    const monthIndex = monthNames.indexOf(month);
    return new Date(year, monthIndex + 1, 0).getDate();
  };

  const getDayName = (date, month, year) => {
    const monthIndex = monthNames.indexOf(month);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dateObj = new Date(year, monthIndex, date);
    return dayNames[dateObj.getDay()];
  };

  const isPublicHoliday = (date, month, year) => Boolean(getHolidayInfo(date, month, year));

  const isSunday = (date, month, year) => {
    return getDayName(date, month, year) === 'Sun';
  };

  const getWorkingDaysInMonth = (month, year) => {
    const totalDays = getDaysInMonth(month, year);
    let workingDays = 0;
    for (let day = 1; day <= totalDays; day++) {
      if (isSunday(day, month, year)) continue;
      if (isPublicHoliday(day, month, year)) continue;
      workingDays++;
    }
    return workingDays;
  };

  const getEmployeeWorkingDays = (employee, month, year) => {

    return getWorkingDaysInMonth(month, year);
  };

  const getPerDaySalary = (employee, month, year) => {
    const workingDays = getEmployeeWorkingDays(employee, month, year);
    return workingDays > 0 ? employee.baseSalary / workingDays : 0;
  };

  // Get quarter from month name
  const getQuarterFromMonth = (month) => {
    const quarterMap = {
      'January': 'Q1', 'February': 'Q1', 'March': 'Q1',
      'April': 'Q2', 'May': 'Q2', 'June': 'Q2',
      'July': 'Q3', 'August': 'Q3', 'September': 'Q3',
      'October': 'Q4', 'November': 'Q4', 'December': 'Q4',
    };
    return quarterMap[month] || 'Q1';
  };

  // Get quarter months array
  const getQuarterMonths = (month) => {
    const quarterMap = {
      'April': ['April', 'May', 'June'],
      'May': ['April', 'May', 'June'],
      'June': ['April', 'May', 'June'],
      'July': ['July', 'August', 'September'],
      'August': ['July', 'August', 'September'],
      'September': ['July', 'August', 'September'],
      'October': ['October', 'November', 'December'],
      'November': ['October', 'November', 'December'],
      'December': ['October', 'November', 'December'],
      'January': ['January', 'February', 'March'],
      'February': ['January', 'February', 'March'],
      'March': ['January', 'February', 'March'],
    };
    return quarterMap[month] || ['January', 'February', 'March'];
  };

  // Fetch quarter leave data from API
  const fetchQuarterLeaveData = async (userId, month, year) => {
    try {
      setQuarterLeaveLoading(true);
      const quarter = getQuarterFromMonth(month);
      const token = localStorage.getItem("token");

      if (!token || !token.trim()) {
        return null;
      }

      const response = await axiosInstance.post('/api/leaves/quarter', {
        userId,
        year,
        quarter
      }, {
        headers: {
          Authorization: `Bearer ${token.trim()}`
        }
      });

      if (response.data && response.data.summary) {
        return {
          ...response.data.summary,
          quarter: response.data.quarter,
          months: response.data.months || [],
          quarterMonths: getQuarterMonths(month)
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching quarter leave data:', error);
      return null;
    } finally {
      setQuarterLeaveLoading(false);
    }
  };

  const getQuarterLeaveStatus = (employeeData, month) => {
    const quarterMonths = getQuarterMonths(month);
    let totalCL = 0, totalPL = 0, totalUnpaid = 0;

    quarterMonths.forEach(m => {
      if (employeeData[m]) {
        totalCL += employeeData[m].cl || 0;
        totalPL += employeeData[m].pl || 0;
        totalUnpaid += employeeData[m].unpaid || 0;
      }
    });

    return { totalCL, totalPL, totalUnpaid, quarterMonths };
  };

  // Calculate quarterly leave balance (remaining CL/PL for the quarter)
  const getQuarterlyLeaveBalance = (employee, month, currentAttendanceData) => {
    const quarterStatus = getQuarterLeaveStatus(employee.monthlyData || {}, month);

    // Count CL and PL in current month's attendance (including unsaved changes)
    const currentMonthCL = currentAttendanceData.filter(d => d.status === 'cl').length;
    const currentMonthPL = currentAttendanceData.filter(d => d.status === 'pl').length;

    // Calculate total used in quarter (excluding current month, then add current month's count)
    const otherMonthsCL = quarterStatus.totalCL - (employee.monthlyData[month]?.cl || 0);
    const otherMonthsPL = quarterStatus.totalPL - (employee.monthlyData[month]?.pl || 0);

    const totalQuarterCL = otherMonthsCL + currentMonthCL;
    const totalQuarterPL = otherMonthsPL + currentMonthPL;

    // Quarterly limits: 2 CL and 2 PL per quarter
    const quarterlyCLLimit = 2;
    const quarterlyPLLimit = 2;

    const remainingCL = Math.max(0, quarterlyCLLimit - totalQuarterCL);
    const remainingPL = Math.max(0, quarterlyPLLimit - totalQuarterPL);

    return {
      usedCL: totalQuarterCL,
      usedPL: totalQuarterPL,
      remainingCL,
      remainingPL,
      canTakeCL: remainingCL > 0,
      canTakePL: remainingPL > 0
    };
  };

  const calculateAttendanceStats = (attendance) => {
    let present = 0, cl = 0, pl = 0, absent = 0, sunday = 0, holiday = 0, extrapay = 0;

    attendance.forEach(day => {
      if (day.status === 'present') present++;
      else if (day.status === 'cl') cl++;
      else if (day.status === 'pl') pl++;
      else if (day.status === 'absent') absent++;
      else if (day.status === 'sunday') sunday++;
      else if (day.status === 'holiday') holiday++;
      else if (day.status === 'extrapay') extrapay++;
    });

    return { present, cl, pl, absent, sunday, holiday, extrapay };
  };

  const calculateSalaryFromAttendance = (employee, month) => {
    const monthData = employee.monthlyData[month];
    if (!monthData) {
      return {
        netSalary: employee.baseSalary,
        deduction: 0,
        extraPay: 0,
        bonus: 0,
        present: 0,
        cl: 0,
        pl: 0,
        unpaid: 0,
        sunday: 0,
        holiday: 0,
        absent: 0,
        extrapay: 0
      };
    }

    const stats = calculateAttendanceStats(monthData.attendance || []);
    const quarterStatus = getQuarterLeaveStatus(employee.monthlyData, month);

    let unpaidLeaves = 0;
    const totalPaidLeavesInQuarter = quarterStatus.totalCL + quarterStatus.totalPL;
    const quarterlyPaidLeaveLimit = 4;

    if (totalPaidLeavesInQuarter > quarterlyPaidLeaveLimit) {
      unpaidLeaves = totalPaidLeavesInQuarter - quarterlyPaidLeaveLimit;
    }
    unpaidLeaves += stats.absent;

    // Use backend calculated values if available, otherwise calculate locally
    const netSalary = monthData.netSalary ?? employee.baseSalary;

    // Calculate deduction: use from DB/API if available, otherwise calculate from unpaid days × per day salary
    let deduction = monthData.deduction ??
      monthData.serverSalary?.deduction ??
      null;

    // Deduction = unpaid days × per day salary
    if ((deduction === null || deduction === undefined) && unpaidLeaves > 0) {
      const perDaySalary = monthData.serverSalary?.perDaySalary ??
        (employee.baseSalary / 25); // Default: monthly salary / 25 working days
      deduction = unpaidLeaves * perDaySalary;
    }

    // Ensure deduction is a number (default to 0 if still null/undefined)
    deduction = deduction ?? 0;
    // Get extraPay from monthData or serverSalary
    const extraPay = monthData.extraPay ??
      monthData.serverSalary?.extraPay ??
      0;
    // Get bonus from monthData or serverSalary - ensure it's a number
    const bonusValue = monthData.bonus ?? monthData.serverSalary?.bonus ?? 0;
    const bonus = parseFloat(bonusValue) || 0;

    return {
      netSalary,
      deduction,
      extraPay,
      bonus,
      present: stats.present,
      cl: stats.cl,
      pl: stats.pl,
      unpaid: unpaidLeaves,
      sunday: stats.sunday,
      holiday: stats.holiday,
      absent: stats.absent,
      extrapay: stats.extrapay
    };
  };

  const openAttendanceModal = async (employee) => {
    // Preserve scroll position before opening modal to prevent background from scrolling to top
    scrollPositionBeforeModalRef.current = window.pageYOffset || document.documentElement.scrollTop || window.scrollY;

    // Store the employee ID being worked on
    lastWorkedEmployeeIdRef.current = employee.id;
    setEditingEmployee(employee);
    const year = getMonthYear(selectedMonth);
    const daysInMonth = getDaysInMonth(selectedMonth, year);

    // Fetch quarter leave data from API
    const quarterData = await fetchQuarterLeaveData(employee.id, selectedMonth, year);
    setQuarterLeaveData(quarterData);

    const existingAttendance = employee.monthlyData[selectedMonth]?.attendance || [];
    const attendance = Array(daysInMonth).fill(null).map((_, i) => {
      const date = i + 1;
      const existing = existingAttendance.find(a => a.date === date);

      if (existing) {
        if (existing.status === 'holiday') {
          const holidayInfo = getHolidayInfo(date, selectedMonth, year);
          return {
            ...existing,
            isHoliday: true,
            holidayName: existing.holidayName || holidayInfo?.name || 'Holiday'
          };
        }
        return existing;
      }

      if (isSunday(date, selectedMonth, year)) {
        return { date, status: 'sunday', dayName: 'Sun', isHoliday: false };
      } else if (isPublicHoliday(date, selectedMonth, year)) {
        const holidayInfo = getHolidayInfo(date, selectedMonth, year);
        return {
          date,
          status: 'holiday',
          dayName: getDayName(date, selectedMonth, year),
          isHoliday: true,
          holidayName: holidayInfo?.name || 'Holiday'
        };
      }

      // For current month, if no existing data, return empty status
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      const monthNumber = getMonthNumber(selectedMonth);
      const isCurrentMonth = monthNumber === currentMonth && year === currentYear;

      return {
        date,
        status: isCurrentMonth ? '' : 'present',
        dayName: getDayName(date, selectedMonth, year),
        isHoliday: false
      };
    });

    setAttendanceData(attendance);
    setShowAttendanceModal(true);
  };

  const handleCloseAttendanceModal = () => {
    const employeeId = lastWorkedEmployeeIdRef.current;
    setShowAttendanceModal(false);
    setEditingEmployee(null);
    setAttendanceError(null);
    setQuarterLeaveData(null);

    // Scroll to the employee card that was being worked on
    // Use multiple attempts to ensure it works after React re-renders and body overflow is restored
    if (employeeId) {
      const scrollToEmployee = () => {
        const cardRef = employeeCardRefs.current[employeeId];
        if (cardRef?.current) {
          const cardTop = cardRef.current.offsetTop;
          const scrollPosition = cardTop - 100; // 100px padding from top
          window.scrollTo({ top: Math.max(0, scrollPosition), behavior: 'auto' });
        }
      };

      // Try immediately
      setTimeout(scrollToEmployee, 50);
      // Try after body overflow is restored
      setTimeout(scrollToEmployee, 150);
      // Try again after React re-renders
      setTimeout(scrollToEmployee, 250);
    }
  };

  // Validate leave totals are configured before saving
  const validateLeaveTotalsConfigured = async (userId, month, year) => {
    try {
      const quarter = getQuarterFromMonth(month);
      const token = localStorage.getItem("token");

      if (!token || !token.trim()) {
        return { isValid: false, error: 'Authentication required' };
      }

      // Try to fetch quarter leave data to check if totals are configured
      const response = await axiosInstance.post('/api/leaves/quarter', {
        userId,
        year,
        quarter
      }, {
        headers: {
          Authorization: `Bearer ${token.trim()}`
        }
      });

      if (response.data && response.data.summary) {
        const summary = response.data.summary;
        const totalCL = summary.totalCL || summary.total_CL || 0;
        const totalPL = summary.totalPL || summary.total_PL || 0;

        if (totalCL === 0 && totalPL === 0) {
          return {
            isValid: false,
            error: `Leave totals not configured for ${quarter} ${year}. Please add total_CL / total_PL first using add/clpl API.`
          };
        }

        return { isValid: true };
      }

      // If no data returned, assume not configured
      return {
        isValid: false,
        error: `Leave totals not configured for ${quarter} ${year}. Please add total_CL / total_PL first using add/clpl API.`
      };
    } catch (error) {
      // If API call fails, try a test salary calculation to check
      // This will fail if leave totals are not configured
      try {
        await axiosInstance.post(`${LOCAL_API_BASE_URL}/salary/calculate`, {
          userId,
          month: getMonthNumber(month),
          year,
        });
        return { isValid: true };
      } catch (calcError) {
        const errorMsg = calcError.response?.data?.message || '';
        if (errorMsg.includes('Leave totals not configured') || errorMsg.includes('add total_CL / total_PL')) {
          const quarter = getQuarterFromMonth(month);
          const quarterMonths = getQuarterMonths(month);
          const quarterMonthsStr = quarterMonths.join(', ');
          return {
            isValid: false,
            error: `Leave totals not configured for ${month} ${year} (${quarter} ${year}). Please add total_CL / total_PL first for the quarter (${quarterMonthsStr}) using add/clpl API.`
          };
        }
        // If it's a different error, allow saving (let backend handle it)
        return { isValid: true };
      }
    }
  };

  const saveAttendance = async () => {
    if (!editingEmployee) return;

    setAttendanceError(null);
    setAttendanceSaving(true);

    const year = getMonthYear(selectedMonth);
    const monthNumber = getMonthNumber(selectedMonth);

    if (monthNumber < 1) {
      setAttendanceError('Invalid month selected.');
      setAttendanceSaving(false);
      return;
    }

    // Validate quarterly limits before saving
    const quarterlyBalance = getQuarterlyLeaveBalance(editingEmployee, selectedMonth, attendanceData);

    if (quarterlyBalance.usedCL > 2) {
      setAttendanceError(`Cannot save: CL limit exceeded for this quarter. You have ${quarterlyBalance.usedCL} CL, but maximum is 2 per quarter.`);
      setAttendanceSaving(false);
      return;
    }

    if (quarterlyBalance.usedPL > 2) {
      setAttendanceError(`Cannot save: PL limit exceeded for this quarter. You have ${quarterlyBalance.usedPL} PL, but maximum is 2 per quarter.`);
      setAttendanceSaving(false);
      return;
    }

    // VALIDATE LEAVE TOTALS ARE CONFIGURED BEFORE SAVING ATTENDANCE
    const leaveValidation = await validateLeaveTotalsConfigured(editingEmployee.id, selectedMonth, year);
    if (!leaveValidation.isValid) {
      setAttendanceError(leaveValidation.error);
      setAttendanceSaving(false);
      return;
    }

    const payload = {
      userId: editingEmployee.id,
      month: monthNumber,
      year,
      attendance: attendanceData.map((day) => ({
        date: formatDateForApi(day.date, selectedMonth, year),
        status: day.status.toUpperCase()
      }))
    };

    try {
      // Save attendance (only if validation passed)
      await axiosInstance.post(`${LOCAL_API_BASE_URL}/save`, payload);

      // Always recalculate salary after saving attendance to ensure it's up-to-date
      // This ensures extraPay and other values reflect the latest attendance changes
      const salaryResponse = await axiosInstance.post(`${LOCAL_API_BASE_URL}/salary/calculate`, {
        userId: editingEmployee.id,
        month: monthNumber,
        year,
      });

      const stats = calculateAttendanceStats(attendanceData);
      const salaryDetails = salaryResponse.data?.salaryDetails || {};

      // Use the calculate API response - it's the most up-to-date after saving attendance
      const parseSafe = (value, fallback = 0) => {
        if (value === null || value === undefined) return fallback;
        const parsed = parseFloat(value);
        return Number.isNaN(parsed) ? fallback : parsed;
      };

      // Always use calculate API response values (they're the most recent)
      // The calculate API just calculated and saved to DB, so these are the latest values
      const finalExtraPay = parseSafe(salaryDetails.extraPay);
      const finalNetSalary = parseSafe(salaryDetails.netSalary, editingEmployee.baseSalary);
      const finalDeduction = parseSafe(salaryDetails.salaryCut);
      const finalPerDaySalary = parseSafe(salaryDetails.perDaySalary);
      const finalMonthlySalary = parseSafe(salaryDetails.monthlySalary, editingEmployee.baseSalary);

      // Fetch the latest salary summary from DB (with small delay to ensure DB write is complete)
      await new Promise(resolve => setTimeout(resolve, 200)); // 200ms delay for DB write
      const latestSalarySummary = await fetchSalarySummaryFromApi(editingEmployee.id, monthNumber, year).catch(() => null);

      // Update employee data - ALWAYS use calculate API response values for extraPay, netSalary, deduction
      const updatedEmployees = employees.map(emp => {
        if (emp.id === editingEmployee.id) {
          // ALWAYS use calculate API response values - they're the most up-to-date after calculation
          const extraPay = finalExtraPay;
          const netSalary = finalNetSalary;
          const deduction = finalDeduction;
          const bonus = latestSalarySummary?.bonus ?? 0;

          // Create serverSalary object with calculate API values (most recent from DB)
          const serverSalaryData = {
            netSalary: netSalary,
            deduction: deduction,
            extraPay: extraPay,
            bonus: bonus,
            perDaySalary: finalPerDaySalary || latestSalarySummary?.perDaySalary,
            monthlySalary: finalMonthlySalary || latestSalarySummary?.monthlySalary || emp.baseSalary
          };

          return {
            ...emp,
            monthlyData: {
              ...emp.monthlyData,
              [selectedMonth]: {
                attendance: attendanceData,
                present: stats.present,
                cl: stats.cl,
                pl: stats.pl,
                absent: stats.absent,
                sunday: stats.sunday,
                holiday: stats.holiday,
                netSalary: netSalary,
                deduction: deduction,
                extraPay: extraPay,
                bonus: bonus,
                serverSalary: serverSalaryData
              }
            }
          };
        }
        return emp;
      });

      const employeeId = editingEmployee.id;
      setEmployees(updatedEmployees);

      // Refresh quarter leave data after saving
      const quarterData = await fetchQuarterLeaveData(employeeId, selectedMonth, year);
      setQuarterLeaveData(quarterData);

      handleCloseAttendanceModal();

      // Scroll to the employee card after saving (with delay to ensure DOM updated)
      setTimeout(() => {
        const cardRef = employeeCardRefs.current[employeeId];
        if (cardRef?.current) {
          const cardTop = cardRef.current.offsetTop;
          const scrollPosition = cardTop - 100; // 100px padding from top
          window.scrollTo({ top: Math.max(0, scrollPosition), behavior: 'auto' });
        }
      }, 200);
    } catch (error) {
      console.error('Error saving attendance:', error);
      let errorMessage = error.response?.data?.message || 'Failed to save attendance.';

      // Format error message to include month name if it mentions quarter
      if (errorMessage.includes('Leave totals not configured')) {
        const quarter = getQuarterFromMonth(selectedMonth);
        const quarterMonths = getQuarterMonths(selectedMonth);
        const quarterMonthsStr = quarterMonths.join(', ');
        errorMessage = `Leave totals not configured for ${selectedMonth} ${year} (${quarter} ${year}). Please add total_CL / total_PL first for the quarter (${quarterMonthsStr}) using add/clpl API.`;
      }

      setAttendanceError(errorMessage);
    } finally {
      setAttendanceSaving(false);
    }
  };

  const updateAttendanceStatus = (date, status) => {
    // Store scroll position before state update
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    // Store modal scroll position using ref
    const modalScrollPosition = modalScrollRef.current?.scrollTop || 0;

    const year = getMonthYear(selectedMonth);
    const isSundayDay = isSunday(date, selectedMonth, year);
    const currentDay = attendanceData.find(d => d.date === date);

    // On Sunday days, only allow Present or Sunday status
    if (isSundayDay && status !== 'present' && status !== 'sunday' && status !== 'extrapay'
    ) {
      setAttendanceError('On Sunday, only Present status is allowed');
      setTimeout(() => setAttendanceError(null), 3000);
      return;
    }

    // Prevent selecting Sunday status on non-Sunday days
    if (status === 'sunday' && !isSundayDay) {
      setAttendanceError('Sunday can only be set on actual Sunday days');
      setTimeout(() => setAttendanceError(null), 3000);
      return;
    }

    // Check quarterly limits for CL and PL
    if (status === 'cl' || status === 'pl') {
      // Calculate what the attendance would look like after this change
      const updatedAttendanceData = attendanceData.map(day =>
        day.date === date ? { ...day, status } : day
      );

      const balance = getQuarterlyLeaveBalance(editingEmployee, selectedMonth, updatedAttendanceData);

      // Check if this change would exceed the quarterly limit
      if (status === 'cl' && balance.usedCL > 2) {
        setAttendanceError(`Maximum 2 CL (Casual Leave) allowed per quarter. This change would result in ${balance.usedCL}/2 CL this quarter.`);
        setTimeout(() => setAttendanceError(null), 4000);
        return;
      }

      if (status === 'pl' && balance.usedPL > 2) {
        setAttendanceError(`Maximum 2 PL (Paid Leave) allowed per quarter. This change would result in ${balance.usedPL}/2 PL this quarter.`);
        setTimeout(() => setAttendanceError(null), 4000);
        return;
      }
    }

    const isHolidayStatus = status === 'holiday';
    const holidayInfo = isHolidayStatus ? getHolidayInfo(date, selectedMonth, year) : null;

    setAttendanceData(prev =>
      prev.map(day => day.date === date ? {
        ...day,
        status,
        isHoliday: isHolidayStatus,
        holidayName: isHolidayStatus ? (holidayInfo?.name || 'Holiday') : undefined
      } : day)
    );

    // Clear any previous errors
    setAttendanceError(null);

    // Restore scroll position after React re-renders (no animation)
    setTimeout(() => {
      window.scrollTo({ top: scrollPosition, behavior: 'auto' });
      // Restore modal scroll position if modal is open
      if (modalScrollRef.current) {
        modalScrollRef.current.scrollTop = modalScrollPosition;
      }
    }, 50);
  };

  const fetchLatestSalary = async (userId) => {
    try {
      const response = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/salary`, {
        params: { userId }
      });
      return extractMonthlySalary(response);
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error(`Failed to fetch latest salary for user ${userId}:`, error);
      }
      return null;
    }
  };

  const updateSalary = async (employeeId, newSalary) => {
    const salaryValue = parseFloat(newSalary);
    if (isNaN(salaryValue) || salaryValue <= 0) {
      setSalaryUpdateState(prev => ({
        ...prev,
        [employeeId]: { error: 'Please enter a valid salary amount' }
      }));
      return;
    }

    // Preserve scroll position before update to prevent jumping to top
    const savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop || window.scrollY;

    // Store employee ID for scrolling after update
    lastWorkedEmployeeIdRef.current = employeeId;

    setSalaryUpdateState(prev => ({
      ...prev,
      [employeeId]: { loading: true, error: null }
    }));

    // Prevent scroll jump during state update
    requestAnimationFrame(() => {
      window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });
    });

    try {
      // Get the selected month and year from the UI
      const yearForSelectedMonth = getMonthYear(selectedMonth);
      const monthNumber = getMonthNumber(selectedMonth);

      // Call API with month and year parameters
      await axiosInstance.post(`${LOCAL_API_BASE_URL}/set/netsalary`, {
        userId: employeeId.toString(),
        monthlySalary: salaryValue.toString(),
        month: monthNumber.toString(),
        year: yearForSelectedMonth.toString()
      });

      const refreshedSalary = await fetchLatestSalary(employeeId);
      const updatedSalary = typeof refreshedSalary === 'number' ? refreshedSalary : salaryValue;

      // Recalculate net salary for selected month after base salary update
      // (yearForSelectedMonth and monthNumber already declared above)

      // Get current employee data to access attendance
      const currentEmployee = employees.find(emp => emp.id === employeeId);
      const currentMonthData = currentEmployee?.monthlyData?.[selectedMonth] || {};
      const currentAttendance = currentMonthData.attendance || [];

      // If there's attendance data, recalculate salary using the API
      let newNetSalary = updatedSalary;
      let newDeduction = currentMonthData.deduction ?? 0;
      let newExtraPay = currentMonthData.extraPay ?? 0;

      if (currentAttendance.length > 0) {
        try {
          // Call salary calculate API to recalculate based on new base salary and attendance
          const salaryResponse = await axiosInstance.post(`${LOCAL_API_BASE_URL}/salary/calculate`, {
            userId: employeeId,
            month: monthNumber,
            year: yearForSelectedMonth,
          });

          const salaryDetails = salaryResponse.data?.salaryDetails || {};
          if (salaryDetails.netSalary !== undefined) {
            newNetSalary = parseFloat(salaryDetails.netSalary) || updatedSalary;
          }
          if (salaryDetails.salaryCut !== undefined) {
            newDeduction = parseFloat(salaryDetails.salaryCut) || 0;
          }
          if (salaryDetails.extraPay !== undefined) {
            newExtraPay = parseFloat(salaryDetails.extraPay) || 0;
          }
        } catch (calcError) {
          console.error('Error recalculating salary:', calcError);
        }
      }

      // Always fetch the latest salary summary from API to ensure we have complete data including extraPay and bonus
      const updatedSalarySummary = await fetchSalarySummaryFromApi(employeeId, monthNumber, yearForSelectedMonth);
      let newBonus = currentMonthData.bonus ?? 0;
      if (updatedSalarySummary) {
        if (updatedSalarySummary.netSalary !== undefined) {
          newNetSalary = updatedSalarySummary.netSalary;
        }
        if (updatedSalarySummary.deduction !== undefined) {
          newDeduction = updatedSalarySummary.deduction;
        }
        if (updatedSalarySummary.extraPay !== undefined) {
          newExtraPay = updatedSalarySummary.extraPay;
        }
        if (updatedSalarySummary.bonus !== undefined) {
          newBonus = updatedSalarySummary.bonus;
        }
      }

      setEmployees(prev => prev.map(emp => {
        if (emp.id === employeeId) {
          return {
            ...emp,
            baseSalary: updatedSalary,
            monthlyData: {
              ...emp.monthlyData,
              [selectedMonth]: {
                ...currentMonthData,
                netSalary: newNetSalary,
                deduction: newDeduction,
                extraPay: newExtraPay,
                bonus: newBonus,
                serverSalary: updatedSalarySummary || {
                  netSalary: newNetSalary,
                  deduction: newDeduction,
                  extraPay: newExtraPay,
                  bonus: newBonus,
                  monthlySalary: updatedSalary
                }
              }
            }
          };
        }
        return emp;
      }));

      // Prevent scroll jump immediately after setEmployees
      requestAnimationFrame(() => {
        window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });
      });

      setSalaryUpdateState(prev => ({
        ...prev,
        [employeeId]: { success: true, loading: false }
      }));

      // Prevent scroll jump during state update and then scroll to employee card
      // Use multiple attempts to ensure scroll stays in place and then moves to correct position
      const scrollToEmployee = () => {
        const cardRef = employeeCardRefs.current[employeeId];
        if (cardRef?.current) {
          const cardTop = cardRef.current.offsetTop;
          const scrollPosition = cardTop - 100; // 100px padding from top
          window.scrollTo({ top: Math.max(0, scrollPosition), behavior: 'auto' });
        } else {
          // If card ref not ready, maintain current scroll position
          window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });
        }
      };

      // First, maintain scroll position immediately after state update
      requestAnimationFrame(() => {
        window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });
      });

      // Then scroll to employee card after DOM updates
      setTimeout(() => {
        scrollToEmployee();
      }, 50);

      setTimeout(() => {
        scrollToEmployee();
      }, 150);

      setTimeout(() => {
        scrollToEmployee();
      }, 250);

      // Clear success message after 2 seconds
      setTimeout(() => {
        setSalaryUpdateState(prev => ({
          ...prev,
          [employeeId]: {}
        }));
      }, 2000);
    } catch (error) {
      console.error('Error updating salary:', error);
      setSalaryUpdateState(prev => ({
        ...prev,
        [employeeId]: {
          error: error.response?.data?.message || 'Failed to update salary',
          loading: false
        }
      }));


    }
  };

  // Fetch existing bonus data
  const fetchExistingBonus = async (userId, month, year) => {
    try {
      const response = await axiosInstance.get(`${LOCAL_API_BASE_URL}/bonus`, {
        params: { userId, month, year }
      });

      if (response.data?.data) {
        return {
          bonus: response.data.data.bonus || 0,
          bonusReason: response.data.data.bonusReason || response.data.data.bonus_reason || ""
        };
      }
      return null;
    } catch (error) {
      // If 404 or no bonus exists, return null
      if (error?.response?.status === 404) {
        return null;
      }
      console.error('Error fetching bonus:', error);
      return null;
    }
  };

  const openBonusModal = useCallback(async (employee) => {
    // Preserve scroll position before opening modal to prevent background from scrolling to top
    scrollPositionBeforeModalRef.current = window.pageYOffset || document.documentElement.scrollTop || window.scrollY;

    // Store the employee ID being worked on
    lastWorkedEmployeeIdRef.current = employee.id;
    setSelectedEmployee(employee);

    // Fetch existing bonus data
    const yearForSelectedMonth = getMonthYear(selectedMonth);
    const monthNumber = getMonthNumber(selectedMonth);
    const existingBonus = await fetchExistingBonus(employee.id, monthNumber, yearForSelectedMonth);

    // Pre-fill form with existing bonus data if available
    if (existingBonus && existingBonus.bonus > 0) {
      setBonus(existingBonus.bonus.toString());
      setBonusReason(existingBonus.bonusReason || "");
      setHasExistingBonus(true);
    } else {
      setBonus("");
      setBonusReason("");
      setHasExistingBonus(false);
    }

    setShowBonusModal(true);
  }, [selectedMonth]);

  const closeBonusModal = useCallback(() => {
    const employeeId = lastWorkedEmployeeIdRef.current;
    setShowBonusModal(false);
    setSelectedEmployee(null);
    setBonus("");
    setBonusReason("");
    setBonusSubmitting(false);

    // Scroll to the employee card that was being worked on
    // Use multiple attempts to ensure it works after React re-renders and body overflow is restored
    if (employeeId) {
      const scrollToEmployee = () => {
        const cardRef = employeeCardRefs.current[employeeId];
        if (cardRef?.current) {
          const cardTop = cardRef.current.offsetTop;
          const scrollPosition = cardTop - 100; // 100px padding from top
          window.scrollTo({ top: Math.max(0, scrollPosition), behavior: 'auto' });
        }
      };

      // Try immediately
      setTimeout(scrollToEmployee, 50);
      // Try after body overflow is restored
      setTimeout(scrollToEmployee, 150);
      // Try again after React re-renders
      setTimeout(scrollToEmployee, 250);
    }
  }, []);


  const submitBonus = useCallback(async () => {
    if (!bonus || parseFloat(bonus) <= 0) {
      alert("Please enter a valid bonus amount");
      return;
    }

    if (!selectedEmployee) {
      alert("No employee selected");
      return;
    }

    setBonusSubmitting(true);

    const year = getMonthYear(selectedMonth);
    const monthNumber = getMonthNumber(selectedMonth);

    const bonusAmount = parseFloat(bonus);
    if (isNaN(bonusAmount) || bonusAmount <= 0) {
      alert("Please enter a valid bonus amount");
      setBonusSubmitting(false);
      return;
    }

    const payload = {
      userId: selectedEmployee.id.toString(),
      month: monthNumber.toString(),
      year: year.toString(),
      bonus: bonusAmount.toString(),
      bonusReason: bonusReason || "",
    };

    console.log('Submitting bonus:', payload);

    try {
      const res = await axiosInstance.post(`${LOCAL_API_BASE_URL}/add/extrabonus`, payload);

      const data = res.data;
      console.log('Bonus API response:', data);

      if (res.status >= 200 && res.status < 300) {
        // Recalculate salary after adding bonus to update netSalary
        try {
          const calcResponse = await axiosInstance.post(`${LOCAL_API_BASE_URL}/salary/calculate`, {
            userId: selectedEmployee.id,
            month: monthNumber,
            year: year,
          });
          console.log('Salary recalculated after bonus:', calcResponse.data);
        } catch (calcError) {
          console.error('Error recalculating salary after bonus:', calcError);
        }

        // Store employee ID before closing modal
        const employeeId = selectedEmployee.id;

        // Close modal first for smooth UX
        setShowBonusModal(false);
        setSelectedEmployee(null);
        setBonus("");
        setBonusReason("");
        setHasExistingBonus(false);
        setBonusSubmitting(false);

        // Refresh employee data for the current month
        const yearForSelectedMonth = getMonthYear(selectedMonth);
        // Use current employees from closure - don't include in deps to prevent re-renders
        attachAttendanceToEmployees(employees, selectedMonth, yearForSelectedMonth)
          .then(updated => {
            setEmployees(updated);
            // Scroll to the employee card that was being worked on
            setTimeout(() => {
              const cardRef = employeeCardRefs.current[employeeId];
              if (cardRef?.current) {
                const cardTop = cardRef.current.offsetTop;
                const scrollPosition = cardTop - 100; // 100px padding from top
                window.scrollTo({ top: Math.max(0, scrollPosition), behavior: 'auto' });
              }
            }, 150);
            // Show success message after refresh
            alert(bonus ? "Bonus Updated Successfully!" : "Bonus Added Successfully!");
          })
          .catch(err => {
            console.error('Error refreshing employees:', err);
            // Scroll to employee even on error
            setTimeout(() => {
              const cardRef = employeeCardRefs.current[employeeId];
              if (cardRef?.current) {
                const cardTop = cardRef.current.offsetTop;
                const scrollPosition = cardTop - 100;
                window.scrollTo({ top: Math.max(0, scrollPosition), behavior: 'auto' });
              }
            }, 150);
            alert(bonus ? "Bonus Updated Successfully!" : "Bonus Added Successfully!");
          });
      } else {
        setBonusSubmitting(false);
        alert(data.message || "Failed to add bonus");
      }
    } catch (error) {
      // axios rejects on non-2xx, so a server-side failure lands here rather
      // than in the else branch above. Surface the server's message.
      console.error('Error adding bonus:', error);
      setBonusSubmitting(false);
      alert(error.response?.data?.message || "Server error. Please try again.");
    }
  }, [bonus, bonusReason, selectedEmployee, selectedMonth, closeBonusModal]);

  const generateSalarySlip = async (employee) => {
    const year = getMonthYear(selectedMonth);
    const monthNumber = getMonthNumber(selectedMonth);

    if (monthNumber < 1) {
      alert('Invalid month selected.');
      return;
    }

    setSalarySlipGenerating(prev => ({
      ...prev,
      [employee.id]: true
    }));

    try {
      const response = await axiosInstance.post(
        `${LOCAL_API_BASE_URL}/downloadsalaryslip/${employee.id}`,
        {
          month: monthNumber,
          year: year
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.status && response.data.filePath) {
        // Open the PDF in a new tab
        window.open(response.data.filePath, '_blank');
      } else {
        alert(response.data?.message || 'Failed to generate salary slip');
      }
    } catch (error) {
      console.error('Error generating salary slip:', error);
      const errorMessage = error.response?.data?.message || 'Failed to generate salary slip. Please try again.';
      alert(errorMessage);
    } finally {
      setSalarySlipGenerating(prev => ({
        ...prev,
        [employee.id]: false
      }));
    }
  };

  const shareSalarySlipWhatsapp = async (employee) => {
    const year = getMonthYear(selectedMonth);
    const monthNumber = getMonthNumber(selectedMonth);

    if (monthNumber < 1) {
      alert('Invalid month selected.');
      return;
    }

    setSalarySlipGenerating(prev => ({
      ...prev,
      [employee.id]: true
    }));

    try {
      const response = await axiosInstance.post(
        `${LOCAL_API_BASE_URL}/downloadsalaryslip/${employee.id}`,
        {
          month: monthNumber,
          year: year
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.status && response.data.whatsappUrl) {
        window.open(response.data.whatsappUrl, '_blank');
      } else if (response.data?.filePath) {
        window.open(response.data.filePath, '_blank');
      } else {
        alert(response.data?.message || 'Failed to prepare WhatsApp slip');
      }
    } catch (error) {
      console.error('Error sharing salary slip on WhatsApp:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to prepare WhatsApp slip. Please try again.';
      alert(errorMessage);
    } finally {
      setSalarySlipGenerating(prev => ({
        ...prev,
        [employee.id]: false
      }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-500';
      case 'cl': return 'bg-yellow-500';
      case 'pl': return 'bg-purple-500';
      case 'absent': return 'bg-red-500';
      case 'sunday': return 'bg-blue-500';
      case 'holiday': return 'bg-orange-500';
      case 'extrapay': return 'bg-amber-500';
      default: return 'bg-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'present': return 'P';
      case 'cl': return 'CL';
      case 'pl': return 'PL';
      case 'absent': return 'A';
      case 'sunday': return 'S';
      case 'holiday': return 'H';
      case 'extrapay': return 'EP';
      default: return '-';
    }
  };

  // Memoize BonusModal to prevent recreation on every render
  const BonusModal = useMemo(() => {
    if (!showBonusModal || !selectedEmployee) return null;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn backdrop-blur-sm"
        onClick={(e) => {
          // Close modal when clicking outside
          if (e.target === e.currentTarget && !bonusSubmitting) {
            closeBonusModal();
          }
        }}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-slideUp transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center rounded-t-2xl">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {hasExistingBonus ? 'Update Bonus' : 'Add Bonus'} - {selectedEmployee.name}
              </h2>
              <p className="text-gray-600 mt-1">{selectedMonth} {getMonthYear(selectedMonth)}</p>
            </div>
            <button
              onClick={closeBonusModal}
              disabled={bonusSubmitting}
              className="text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Bonus Amount (₹) <span className="text-red-500">*</span>
              </label>
              <input
                ref={bonusInputRef}
                type="number"
                value={bonus}
                onChange={(e) => setBonus(e.target.value)}
                placeholder="Enter bonus amount"
                disabled={bonusSubmitting}
                min="0"
                step="0"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !bonusSubmitting && bonus) {
                    submitBonus();
                  }
                }}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={bonusReason}
                onChange={(e) => setBonusReason(e.target.value)}
                placeholder="Enter reason for bonus"
                rows={3}
                disabled={bonusSubmitting}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={closeBonusModal}
                disabled={bonusSubmitting}
                className="px-6 py-3 border border-blue-200 text-blue-800 rounded-lg hover:bg-blue-50 hover:shadow transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={submitBonus}
                disabled={bonusSubmitting || !bonus || parseFloat(bonus) <= 0}
                className={`px-6 py-3 rounded-lg flex items-center gap-2 text-sm font-semibold text-white shadow transition-all ${bonusSubmitting || !bonus || parseFloat(bonus) <= 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 hover:shadow-md'
                  }`}
              >
                {bonusSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {hasExistingBonus ? 'Update Bonus' : 'Add Bonus'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }, [showBonusModal, selectedEmployee, bonus, bonusReason, bonusSubmitting, selectedMonth, closeBonusModal, submitBonus]);

  // Handle escape key and body scroll separately
  useEffect(() => {
    if (!showBonusModal) return;

    // Preserve scroll position when modal opens to prevent background from scrolling
    const savedScrollPosition = scrollPositionBeforeModalRef.current || (window.pageYOffset || document.documentElement.scrollTop || window.scrollY);

    // Restore scroll position immediately to prevent jump to top
    window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });

    // Auto-focus input when modal opens
    const timer = setTimeout(() => {
      bonusInputRef.current?.focus();
      // Ensure scroll position is maintained after focus
      window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });
    }, 150);

    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !bonusSubmitting) {
        closeBonusModal();
      }
    };
    document.addEventListener('keydown', handleEscape);

    // Prevent body scroll while preserving scroll position
    const originalOverflow = document.body.style.overflow;
    const originalScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    document.body.style.overflow = 'hidden';

    // Restore scroll position after hiding overflow to prevent jump
    setTimeout(() => {
      window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = originalOverflow;
      // Restore scroll position when modal closes
      setTimeout(() => {
        window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });
      }, 0);
    };
  }, [showBonusModal, bonusSubmitting, closeBonusModal]);

  // Handle body scroll for attendance modal
  useEffect(() => {
    if (!showAttendanceModal) return;

    // Preserve scroll position when modal opens to prevent background from scrolling
    const savedScrollPosition = scrollPositionBeforeModalRef.current || (window.pageYOffset || document.documentElement.scrollTop || window.scrollY);

    // Restore scroll position immediately to prevent jump to top
    window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });

    // Prevent body scroll while preserving scroll position
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Restore scroll position after hiding overflow to prevent jump
    setTimeout(() => {
      window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });
    }, 0);

    return () => {
      document.body.style.overflow = originalOverflow;
      // Restore scroll position when modal closes
      setTimeout(() => {
        window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });
      }, 0);
    };
  }, [showAttendanceModal]);

  const AttendanceModal = () => {
    if (!showAttendanceModal || !editingEmployee) return null;

    const stats = calculateAttendanceStats(attendanceData);
    const year = getMonthYear(selectedMonth);
    const quarterlyBalance = getQuarterlyLeaveBalance(editingEmployee, selectedMonth, attendanceData);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div ref={modalScrollRef} className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Mark Attendance - {editingEmployee.name}
              </h2>
              <p className="text-gray-600 mt-1">{selectedMonth} {year}</p>
            </div>
            <button
              onClick={handleCloseAttendanceModal}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            {attendanceError && (
              <div className={`mb-4 rounded-lg border p-4 ${attendanceError.includes('Leave totals not configured')
                ? 'border-amber-300 bg-amber-50'
                : 'border-red-200 bg-red-50'
                }`}>
                <div className={attendanceError.includes('Leave totals not configured') ? 'text-amber-800' : 'text-red-700'}>
                  {attendanceError.includes('Leave totals not configured') ? (
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="text-xl">⚠️</span>
                        <div className="flex-1">
                          <p className="font-semibold mb-1">Leave Balance Not Configured</p>
                          <p className="text-sm">{attendanceError}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm">{attendanceError}</p>
                  )}
                </div>
              </div>
            )}

            <div className=" p-4  flex flex-wrap items-center justify-between gap-4">
              <div></div>

              <div className="flex flex-wrap justify-end gap-3">
                <button
                  onClick={handleCloseAttendanceModal}
                  className="px-6 py-3 border border-blue-200 text-blue-800 rounded-lg hover:bg-white hover:shadow transition-all text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAttendance}
                  disabled={attendanceSaving}
                  className={`px-6 py-3 rounded-lg flex items-center gap-2 text-sm font-semibold text-white shadow transition-all ${attendanceSaving
                    ? 'bg-blue-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 hover:shadow-md'
                    }`}
                >
                  <Save size={18} />
                  {attendanceSaving ? 'Saving...' : 'Save Attendance'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-6 gap-3 mb-6">
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">Present</p>
                <p className="text-2xl font-bold text-green-600">{stats.present}</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">CL</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.cl}</p>
                <p className="text-xs text-gray-500 mt-1">Q: {quarterlyBalance.usedCL}/2</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">PL</p>
                <p className="text-2xl font-bold text-purple-600">{stats.pl}</p>
                <p className="text-xs text-gray-500 mt-1">Q: {quarterlyBalance.usedPL}/2</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">Absent</p>
                <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">Sunday</p>
                <p className="text-2xl font-bold text-blue-600">{stats.sunday}</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">Holiday</p>
                <p className="text-2xl font-bold text-orange-600">{stats.holiday}</p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-4 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>Present</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span>Casual Leave (CL)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-purple-500 rounded"></div>
                <span>Paid Leave (PL)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span>Absent (Unpaid)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span>Sunday</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span>Public Holiday</span>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-3 mb-6">
              {attendanceData.map((day) => {
                const isSundayDay = isSunday(day.date, selectedMonth, getMonthYear(selectedMonth));
                return (
                  <div
                    key={day.date}
                    className={`border-2 rounded-lg p-3 transition-colors ${isSundayDay
                      ? 'border-red-400 hover:border-red-500'
                      : 'border-gray-200 hover:border-blue-300'
                      }`}
                  >
                    <div className="text-center mb-2">
                      <p className="text-sm font-bold text-gray-800">{day.date}</p>
                      <p className="text-xs text-gray-500 font-semibold">{day.dayName}</p>
                      {day.isHoliday && (
                        <p className="text-xs text-orange-600 font-semibold">
                          {day.holidayName || 'Holiday'}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      {isSunday(day.date, selectedMonth, getMonthYear(selectedMonth)) ? (
                        // Sunday days - only allow Present or Sunday
                        <>
                          {/* <button
                            onClick={() => updateAttendanceStatus(day.date, 'present')}
                            className={`text-xs py-1.5 px-2 rounded font-medium transition-all ${day.status === 'present'
                              ? 'bg-green-500 text-white shadow-md'
                              : day.status === ''
                                ? 'bg-gray-50 text-gray-400 border-2 border-dashed border-gray-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                            Present
                          </button> */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              updateAttendanceStatus(day.date, 'sunday');
                            }}
                            className={`text-xs py-1.5 px-2 rounded font-medium transition-all ${day.status === 'sunday'
                              ? 'bg-blue-500 text-white shadow-md'
                              : day.status === ''
                                ? 'bg-gray-50 text-gray-400 border-2 border-dashed border-gray-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                            Sunday
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              updateAttendanceStatus(day.date, 'extrapay');
                            }}
                            className={`text-xs py-1.5 px-2 rounded font-medium transition-all ${day.status === 'extrapay'
                              ? 'bg-amber-500 text-white shadow-md'
                              : day.status === ''
                                ? 'bg-gray-50 text-gray-400 border-2 border-dashed border-gray-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                            Present( extra pay)
                          </button>
                        </>
                      ) : (
                        // Non-Sunday days - show all options
                        <>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              updateAttendanceStatus(day.date, 'present');
                            }}
                            className={`text-xs py-1.5 px-2 rounded font-medium transition-all ${day.status === 'present'
                              ? 'bg-green-500 text-white shadow-md'
                              : day.status === ''
                                ? 'bg-gray-50 text-gray-400 border-2 border-dashed border-gray-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                            Present
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              updateAttendanceStatus(day.date, 'cl');
                            }}
                            disabled={!quarterlyBalance.canTakeCL && day.status !== 'cl'}
                            className={`text-xs py-1.5 px-2 rounded font-medium transition-all ${day.status === 'cl'
                              ? 'bg-yellow-500 text-white shadow-md'
                              : !quarterlyBalance.canTakeCL && day.status !== 'cl'
                                ? 'bg-gray-300 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            title={!quarterlyBalance.canTakeCL && day.status !== 'cl' ? `Maximum 2 CL allowed per quarter. Used: ${quarterlyBalance.usedCL}/2` : ''}
                          >
                            CL
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              updateAttendanceStatus(day.date, 'pl');
                            }}
                            disabled={!quarterlyBalance.canTakePL && day.status !== 'pl'}
                            className={`text-xs py-1.5 px-2 rounded font-medium transition-all ${day.status === 'pl'
                              ? 'bg-purple-500 text-white shadow-md'
                              : !quarterlyBalance.canTakePL && day.status !== 'pl'
                                ? 'bg-gray-300 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            title={!quarterlyBalance.canTakePL && day.status !== 'pl' ? `Maximum 2 PL allowed per quarter. Used: ${quarterlyBalance.usedPL}/2` : ''}
                          >
                            PL
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              updateAttendanceStatus(day.date, 'absent');
                            }}
                            className={`text-xs py-1.5 px-2 rounded font-medium transition-all ${day.status === 'absent'
                              ? 'bg-red-500 text-white shadow-md'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                            Absent
                          </button>
                          {/* <button
                            onClick={() => updateAttendanceStatus(day.date, 'holiday')}
                            className={`text-xs py-1.5 px-2 rounded font-medium transition-all ${day.status === 'holiday'
                              ? 'bg-orange-500 text-white shadow-md'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                            Holiday
                          </button> */}
                          {day.status === 'holiday' && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                updateAttendanceStatus(day.date, 'holiday');
                              }}
                              className={`text-xs py-1.5 px-2 rounded font-medium transition-all ${day.status === 'holiday'
                                ? 'bg-orange-500 text-white shadow-md'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                              Holiday
                            </button>
                          )}

                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </div>
    );
  };

  const EmployeeCard = ({ employee, cardRef }) => {
    const [salaryInput, setSalaryInput] = useState(employee.baseSalary.toString());
    const [selectedMonthForBreakdown, setSelectedMonthForBreakdown] = useState(null);
    const [monthBreakdownData, setMonthBreakdownData] = useState({});
    const [loadingMonthData, setLoadingMonthData] = useState(false);

    // Get monthData first before using it
    const monthData = employee.monthlyData[selectedMonth];

    const { netSalary, deduction, extraPay, bonus, present, cl, pl, unpaid, sunday, holiday, absent } = calculateSalaryFromAttendance(employee, selectedMonth);

    // Debug: Log bonus value to help troubleshoot
    if (bonus > 0) {
      console.log(`Bonus for ${employee.name}:`, bonus, 'from monthData:', monthData?.bonus, 'from serverSalary:', monthData?.serverSalary?.bonus);
    }
    // Use API data if available (from attendance modal), otherwise fallback to local calculation
    const quarterStatus = quarterLeaveData && editingEmployee?.id === employee.id ? {
      totalCL: quarterLeaveData.cl || 0,
      totalPL: quarterLeaveData.pl || 0,
      totalUnpaid: Math.max(0, (quarterLeaveData.cl || 0) + (quarterLeaveData.pl || 0) - 4),
      quarterMonths: quarterLeaveData.quarterMonths || getQuarterMonths(selectedMonth)
    } : getQuarterLeaveStatus(employee.monthlyData, selectedMonth);

    // Get per-month breakdown for the quarter
    const quarterMonths = quarterStatus.quarterMonths || getQuarterMonths(selectedMonth);

    // Fetch individual month data when clicking on a month
    const fetchMonthBreakdown = async (monthName) => {
      // If already loaded in monthlyData, use it
      if (employee.monthlyData[monthName]?.cl !== undefined && employee.monthlyData[monthName]?.pl !== undefined) {
        setMonthBreakdownData(prev => ({
          ...prev,
          [monthName]: {
            cl: employee.monthlyData[monthName].cl || 0,
            pl: employee.monthlyData[monthName].pl || 0
          }
        }));
        return;
      }

      // Otherwise fetch from API
      try {
        setLoadingMonthData(true);
        const monthNumber = getMonthNumber(monthName);
        const year = getMonthYear(selectedMonth);

        const apiRecords = await fetchAttendanceFromApi(employee.id, monthNumber, year);

        if (apiRecords.length > 0) {
          const helpers = {
            getDayName,
            getHolidayInfo
          };
          const attendanceEntries = transformApiAttendance(apiRecords, monthName, year, helpers);
          const stats = calculateAttendanceStats(attendanceEntries);

          setMonthBreakdownData(prev => ({
            ...prev,
            [monthName]: {
              cl: stats.cl || 0,
              pl: stats.pl || 0
            }
          }));
        } else {
          // No data found, set to 0
          setMonthBreakdownData(prev => ({
            ...prev,
            [monthName]: {
              cl: 0,
              pl: 0
            }
          }));
        }
      } catch (error) {
        console.error(`Error fetching month breakdown for ${monthName}:`, error);
        setMonthBreakdownData(prev => ({
          ...prev,
          [monthName]: {
            cl: 0,
            pl: 0
          }
        }));
      } finally {
        setLoadingMonthData(false);
      }
    };

    const handleMonthClick = (monthName) => {
      if (selectedMonthForBreakdown === monthName) {
        // Clicking same month again - close it
        setSelectedMonthForBreakdown(null);
      } else {
        // Clicking different month - fetch data and show
        setSelectedMonthForBreakdown(monthName);
        fetchMonthBreakdown(monthName);
      }
    };

    const year = getMonthYear(selectedMonth);
    const workingDays = getEmployeeWorkingDays(employee, selectedMonth, year);
    const perDaySalary = getPerDaySalary(employee, selectedMonth, year);
    const salaryState = salaryUpdateState[employee.id] || {};
    // Use netSalary from API response (after deductions) - this is the actual net salary
    // Fallback to baseSalary if netSalary is not available
    const displayNetSalary = monthData?.netSalary ??
      monthData?.serverSalary?.netSalary ??
      netSalary ??
      employee.baseSalary;

    useEffect(() => {
      setSalaryInput(employee.baseSalary.toString());
    }, [employee.baseSalary]);
    const weekOrder = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const groupedByDay = weekOrder.reduce((acc, day) => {
      acc[day] = monthData?.attendance?.filter(
        (item) => item.dayName === day
      ) || [];
      return acc;
    }, {});



    return (
      <div
        ref={cardRef}
        className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer"

      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="relative w-16 h-16 flex items-center justify-center">
              {employee.avatar && employee.avatar.startsWith('http') ? (
                <img
                  src={employee.avatar}
                  alt={employee.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.parentElement.querySelector('.avatar-fallback');
                    if (fallback) fallback.style.display = 'block';
                  }}
                />
              ) : null}
              <div className={`text-4xl avatar-fallback ${employee.avatar && employee.avatar.startsWith('http') ? 'hidden absolute' : ''}`}>
                {employee.avatar && !employee.avatar.startsWith('http') ? employee.avatar : '👤'}
              </div>
            </div>
            <div>
              <h3 className="font-bold text-lg text-gray-800">{employee.name}</h3>
              {employee.designation && (
                <p className="text-xs text-gray-500">{employee.designation}</p>
              )}
              <p className="text-sm text-gray-500">Base: ₹{employee.baseSalary.toLocaleString()}/month</p>
              <p className="text-xs text-gray-500">Per Day: ₹{Math.round(perDaySalary)} ({workingDays} working days)</p>
            </div>
          </div>
          <div className="flex flex-col gap-2">

            <button
              onClick={(e) => {
                e.stopPropagation();
                openAttendanceModal(employee);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-2"
            >
              <Edit2 size={16} />
              Mark Attendance
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openBonusModal(employee);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-2"
            >
              Bonus +
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                generateSalarySlip(employee);
              }}
              disabled={salarySlipGenerating[employee.id]}
              className={`px-3 py-2 rounded-lg transition-colors text-sm flex items-center gap-1.5 shadow-sm font-medium ${salarySlipGenerating[employee.id]
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              title="Download PDF Salary Slip"
            >
              <Download size={15} />
              {salarySlipGenerating[employee.id] ? 'Generating...' : 'Slip PDF'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                shareSalarySlipWhatsapp(employee);
              }}
              disabled={salarySlipGenerating[employee.id]}
              className={`px-3 py-2 rounded-lg transition-colors text-sm flex items-center gap-1.5 shadow-sm font-medium ${salarySlipGenerating[employee.id]
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-[#25D366] text-white hover:bg-[#1ebd5a]'
                }`}
              title="Send Salary Slip via WhatsApp"
            >
              <MessageCircle size={15} />
              WhatsApp
            </button>
          </div>

        </div>

        {/* Salary Update Section */}
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-2">Set Monthly Salary</label>
          <div className="flex gap-2">
            <input
              type="number"
              value={salaryInput}
              onChange={(e) => {
                e.stopPropagation();
                setSalaryInput(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              placeholder="Enter salary"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                updateSalary(employee.id, salaryInput);
              }}
              disabled={salaryState.loading}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${salaryState.loading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
                }`}
            >
              {salaryState.loading ? 'Updating...' : salaryState.success ? <Check size={16} /> : 'Update'}
            </button>
          </div>
          {salaryState.error && (
            <p className="text-xs text-red-600 mt-2">{salaryState.error}</p>
          )}
          {salaryState.success && (
            <p className="text-xs text-green-600 mt-2">Salary updated successfully!</p>
          )}
        </div>

        {/* Attendance Calendar View */}
        {monthData && monthData.attendance && (
          <div className="mb-4">
            <p
              className="text-xs font-semibold text-gray-600 mb-2 cursor-pointer hover:text-blue-600 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                openAttendanceModal(employee);
              }}
            >
              Monthly Attendance Overview
            </p>
            <div
              className="grid grid-cols-7 gap-1 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                openAttendanceModal(employee);
              }}
            >
              {/* {monthData.attendance.map((day) => (
                <div
                  key={day.date}
                  className={`${getStatusColor(day.status)} text-white text-xs rounded p-1.5 text-center font-semibold shadow-sm hover:opacity-90 transition-opacity`}
                  title={`${day.date} ${selectedMonth} (${day.dayName}): ${day.status}${day.holidayName ? ` - ${day.holidayName}` : ''}`}
                >
                  <div>{day.date}</div>
                  <div className="text-[10px]">{getStatusLabel(day.status)}</div>
                </div>
              ))} */}
              {weekOrder.map((dayName) => (
                <div key={dayName} className={`text-center font-bold text-sm ${dayName === "Sun" ? "text-red-600" : "text-gray-700"
                  }`}>
                  {dayName}



                  {/* Day Attendance Boxes */}
                  <div className="flex flex-col gap-2">
                    {groupedByDay[dayName].map((day) => (
                      <div
                        key={day.date}
                        className={`${getStatusColor(day.status)} text-white text-xs rounded p-1.5 text-center font-semibold shadow-sm`}
                        title={`${day.date} ${selectedMonth} (${day.dayName}): ${day.status}`}
                      >
                        <div>{day.date}</div>
                        <div className="text-[10px]">
                          {getStatusLabel(day.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">Base Salary</p>
            <p className="text-lg font-bold text-blue-600">₹{employee.baseSalary.toLocaleString()}</p>
          </div>
          {extraPay > 0 && (
            <div className="bg-amber-50 p-3 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Extra Pay</p>
              <p className="text-lg font-bold text-amber-600">₹{extraPay.toLocaleString()}</p>
            </div>
          )}
          <div className="bg-green-50 p-3 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">Net Salary</p>
            <p className="text-lg font-bold text-green-600">
              ₹{displayNetSalary.toLocaleString()}
              {bonus > 0 && (
                <span
                  className="text-sm font-normal text-green-700 ml-2 cursor-help"
                  title={monthData?.serverSalary?.bonusReason
                    ? `${monthData.serverSalary.bonusReason} `
                    : monthData?.bonusReason
                      ? `${monthData.bonusReason} `
                      : `Bonus `}
                >
                  (Bonus: ₹{bonus.toLocaleString()})
                </span>
              )}
            </p>
            {(bonus > 0 || extraPay > 0) && (
              <p className="text-xs text-gray-500 mt-1">
                Total: ₹{(displayNetSalary + bonus + extraPay).toLocaleString()}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-6 gap-2 mb-4">
          <div className="bg-green-50 p-2 rounded text-center">
            <p className="text-xs text-gray-600">Present</p>
            <p className="text-lg font-bold text-green-600">{present}</p>
          </div>
          <div className="bg-yellow-50 p-2 rounded text-center">
            <p className="text-xs text-gray-600">CL</p>
            <p className="text-lg font-bold text-yellow-600">{cl}</p>
          </div>
          <div className="bg-purple-50 p-2 rounded text-center">
            <p className="text-xs text-gray-600">PL</p>
            <p className="text-lg font-bold text-purple-600">{pl}</p>
          </div>
          <div className="bg-red-50 p-2 rounded text-center">
            <p className="text-xs text-gray-600">Absent</p>
            <p className="text-lg font-bold text-red-600">{absent}</p>
          </div>
          <div className="bg-blue-50 p-2 rounded text-center">
            <p className="text-xs text-gray-600">Sunday</p>
            <p className="text-xs font-bold text-blue-600">{sunday}</p>
          </div>
          <div className="bg-orange-50 p-2 rounded text-center">
            <p className="text-xs text-gray-600">Holiday</p>
            <p className="text-xs font-bold text-orange-600">{holiday}</p>
          </div>
        </div>

        {deduction > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
            <p className="text-sm text-red-700 font-semibold">
              ⚠️ Salary Deduction: ₹{deduction.toLocaleString()} ({unpaid} unpaid days)
            </p>
            <p className="text-xs text-red-600 mt-1">
              • {absent} direct absent days + {Math.max(0, (quarterStatus.totalCL + quarterStatus.totalPL - 4))} excess quarterly leaves
            </p>
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-amber-800">
              Quarter Status ({quarterStatus.quarterMonths.map((month, idx) => (
                <span key={month}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMonthClick(month);
                    }}
                    className={`hover:underline transition-all ${selectedMonthForBreakdown === month
                      ? 'font-bold text-amber-900 underline'
                      : 'text-amber-800'
                      }`}
                  >
                    {month}
                  </button>
                  {idx < quarterStatus.quarterMonths.length - 1 && ', '}
                </span>
              ))})
            </p>
            {quarterLeaveLoading && editingEmployee?.id === employee.id && (
              <span className="text-xs text-amber-600 animate-pulse">Loading...</span>
            )}
            {quarterLeaveData && editingEmployee?.id === employee.id && (
              <span className="text-xs text-green-600">✓ From API</span>
            )}
          </div>
          <div className="flex gap-4 text-xs flex-wrap mb-2">
            <span className="text-gray-700">Total CL: <strong>{quarterStatus.totalCL}</strong></span>
            <span className="text-gray-700">Total PL: <strong>{quarterStatus.totalPL}</strong></span>
            <span className="text-gray-700">Total Paid: <strong>{quarterStatus.totalCL + quarterStatus.totalPL}/4</strong></span>
            <span className="text-gray-700">Unpaid: <strong className="text-red-600">{quarterStatus.totalUnpaid}</strong></span>
            {quarterLeaveData && editingEmployee?.id === employee.id && (
              <>
                <span className="text-gray-700">Present: <strong>{quarterLeaveData.present || 0}</strong></span>
                <span className="text-gray-700">Absent: <strong>{quarterLeaveData.absent || 0}</strong></span>
                <span className="text-gray-700">Sunday: <strong>{quarterLeaveData.sunday || 0}</strong></span>
                <span className="text-gray-700">Holiday: <strong>{quarterLeaveData.holiday || 0}</strong></span>
              </>
            )}
          </div>

          {/* Per-Month Breakdown - Show when a month is clicked */}
          {selectedMonthForBreakdown && (
            <div className="mt-3 pt-3 border-t border-amber-300 bg-white rounded p-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-amber-800">{selectedMonthForBreakdown} Breakdown:</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMonthForBreakdown(null);
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              {loadingMonthData ? (
                <div className="text-xs text-gray-500 animate-pulse">Loading...</div>
              ) : (
                <div className="flex gap-4 text-xs">
                  <span className="text-gray-700">
                    CL: <strong className={`${monthBreakdownData[selectedMonthForBreakdown]?.cl > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
                      {monthBreakdownData[selectedMonthForBreakdown]?.cl ?? (employee.monthlyData[selectedMonthForBreakdown]?.cl || 0)}
                    </strong>
                  </span>
                  <span className="text-gray-700">
                    PL: <strong className={`${monthBreakdownData[selectedMonthForBreakdown]?.pl > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                      {monthBreakdownData[selectedMonthForBreakdown]?.pl ?? (employee.monthlyData[selectedMonthForBreakdown]?.pl || 0)}
                    </strong>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading employees...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isAuthError = error.includes('token') || error.includes('login') || error.includes('Access Denied');
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <p className="text-red-600 font-semibold mb-4">{error}</p>
          <div className="flex gap-3 justify-center">
            {isAuthError ? (
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to Login
              </button>
            ) : (
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navbar />
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-xl shadow-2xl border border-gray-100 p-6 mb-6 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 text-white ">
            <label className="flex items-center gap-2 text-base font-semibold  mb-4">
              <Calendar className="" size={20} />
              <span>Select Month & Year</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm  mb-2">Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all  cursor-pointer text-black"
                >
                  {financialYearMonths.map((month) => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-2">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-gray-300 cursor-pointer text-black"
                >
                  {availableYears.map((yearOption) => (
                    <option key={yearOption} value={yearOption}>
                      {yearOption}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {employees.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <Users className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 text-lg">No employees found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {employees.map((employee) => {
                // Create ref if it doesn't exist
                if (!employeeCardRefs.current[employee.id]) {
                  employeeCardRefs.current[employee.id] = React.createRef();
                }
                return (
                  <EmployeeCard
                    key={employee.id}
                    employee={employee}
                    cardRef={employeeCardRefs.current[employee.id]}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AttendanceModal />
      {BonusModal}
    </div>
  );
};

export default SalaryCalculationSystem;
